import React, { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { fetchManageInfo, User } from "../../api/manageInfo";

interface UserContextType {
  user: User | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isActive: boolean;
  isUserChecked: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [isUserChecked, setIsUserChecked] = useState(false);
  const isRefreshing = useRef(false);

  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem("isAuthenticated") || loading || isRefreshing.current) return;
    
    if (user && !isRefreshing.current) return;

    isRefreshing.current = true;
    setLoading(true);
    try {
      const userInfo = await fetchManageInfo();
      if (userInfo) {
        setUser(userInfo);
      } else {
        localStorage.removeItem("isAuthenticated");
        setUser(null);
      }
    } catch (error) {
      setUser(null);
      localStorage.removeItem("isAuthenticated");
    } finally {
      setLoading(false);
      setIsUserChecked(true);
      isRefreshing.current = false;
    }
  }, [loading, user]);

  useEffect(() => {
    if (localStorage.getItem("isAuthenticated") && !isRefreshing.current && !user && !loading) {
      refreshUser();
    } else if (!localStorage.getItem("isAuthenticated")) {
      setUser(null);
      setIsUserChecked(true);
    }
  }, [refreshUser, user, loading]);

  const isActive = useMemo(() => !!(
    user &&
    user.isEmailConfirmed &&
    user.isPhoneConfirmed &&
    user.nom &&
    user.prenom &&
    user.magasinId
  ), [user]);

  const value = useMemo(() => ({
    user,
    loading,
    refreshUser,
    setUser,
    isActive,
    isUserChecked
  }), [user, loading, refreshUser, isActive, isUserChecked]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};